import { CANDIDATE_DRAWERS, CANDIDATE_PROFILE_CONFIG } from 'features/inventory-profiles/constants';
import {
  buildOptOutDetail,
  buildReferenceReleaseDetail,
  formatLocation
} from 'features/inventory-profiles/utils';
import strings from 'strings';
import { formatResponseBuilder } from 'UI/utils/inventory';

const COPIES = strings.inventoryProfiles;

const JOB_ORDER_KEYWORDS_DEFINITION = [
  {
    entry: 'company_name',
    valuesToAttach: [
      { name: 'id', path: 'company.id' },
      { name: 'name', path: 'company.name' }
    ]
  }
];

export const handleJobOrderResponseChange = response =>
  formatResponseBuilder(response)(JOB_ORDER_KEYWORDS_DEFINITION);

export const formatReferenceCheckResponse = response => {
  const formattedData = (response.data?.data ?? []).map(item => ({
    ...item,
    referenceStatus: {
      type: item.progress.key,
      totalAnsweredQuestions: item.progress.current,
      totalQuestions: item.progress.total
    }
  }));

  return {
    data: {
      ...response.data,
      data: formattedData
    }
  };
};

export const buildPresentationMakerCandidateParam = candidateData => {
  if (!candidateData?.id) return null;
  const { id, personalInformation, title, specialty, subspecialty } = candidateData;
  const { address } = personalInformation || {};
  return {
    candidate: {
      id,
      title,
      industry: specialty?.industry?.title,
      specialty: specialty?.title,
      subspecialty: subspecialty?.title,
      state: address?.city?.state?.title,
      city: address?.city?.title,
      postalCode: address?.zip
    }
  };
};

const findCandidateType = ({ candidateTypes, dataSheet }) => {
  const typeId = dataSheet?.data?.qualification?.candidateType?.id;
  return candidateTypes.find(type => type.id === typeId);
};

/**
 * The only code that knows the Candidate API shape.
 * @param {Object} params
 * @param {Object} params.candidate Profile with `referenceReleases` attached by useCandidateProfile
 * @param {Object} [params.dataSheet] Candidate data sheet
 * @param {Array} [params.candidateTypes] Catalog of candidate types (`title` and `color` for the chip)
 * @returns {{ header: Object, summary: Object, status: Object }}
 */
export const formatCandidateResponse = ({ candidate, dataSheet, candidateTypes = [] }) => {
  const qualification = dataSheet?.data?.qualification;

  return {
    header: {
      entityType: CANDIDATE_PROFILE_CONFIG.type,
      title: candidate.personalInformation?.full_name,
      lastActivity: candidate.last_activity_date
    },
    summary: {
      company: { id: candidate.company_id, name: candidate.current_company },
      location: formatLocation(candidate.personalInformation?.address?.city),
      created: {
        date: candidate.created_at,
        by: candidate.createdBy?.personalInformation?.full_name
      },
      details: [
        buildReferenceReleaseDetail(candidate.referenceReleases),
        buildOptOutDetail(candidate)
      ]
    },
    status: {
      inventoryType: findCandidateType({ candidateTypes, dataSheet }),
      itemStatus: dataSheet ? qualification?.candidateStatus : candidate.status,
      subStatus: candidate.subStatus,
      optOutStatus: candidate.optOutStatus,
      emailValidationStatus: candidate.emailValidation?.verdict?.name,
      otherEmailValidationStatus: candidate.personalEmailValidation?.verdict?.name,
      entityTypeId: CANDIDATE_PROFILE_CONFIG.id
    }
  };
};

/**
 * Candidate ⋮ menu, in V1 order.
 * @param {Object} params
 * @param {Object} params.permissions Result of useCandidatePermissions
 * @param {boolean} params.canManage From useProfileAccountability
 * @param {boolean} params.canView From useProfileAccountability
 * @param {boolean} params.canCollaborate From useProfileAccountability
 * @param {boolean} params.hasAssistant From useProfileAccountability: true when a collaborator exists
 * @param {Array} params.searchProjectMenuItems `SearchProjectsMenuItems` of useSearchProjectInProfiles
 * @param {Array} params.restrictionMenuItems `menuItems` of useProfileRestrictions
 * @param {(name: string) => () => void} params.openDrawer From useProfileDrawer
 * @param {() => void} params.onOpenDataSheet `openDataSheet` of useCandidateActions
 * @param {() => void} params.onOpenReferenceRelease `openReferenceRelease` of useCandidateActions
 * @param {() => void} params.onUnlinkHiringAuthority `unlinkHiringAuthority` of useCandidateActions
 * @returns {Array<{ title: string, action: () => void, visible: boolean }>}
 */
export const buildCandidateMenuItems = ({
  permissions,
  canManage,
  canView,
  canCollaborate,
  hasAssistant,
  searchProjectMenuItems,
  restrictionMenuItems,
  openDrawer,
  onOpenDataSheet,
  onOpenReferenceRelease,
  onUnlinkHiringAuthority
}) => [
  {
    title: COPIES.common.editProfile,
    action: openDrawer(CANDIDATE_DRAWERS.Edit),
    visible: permissions.canEdit
  },
  {
    title: strings.candidates.editDataSheet.contentPageTitle,
    action: onOpenDataSheet,
    visible: permissions.canEdit
  },
  {
    title: COPIES.menu.verifyUrgency,
    action: openDrawer(CANDIDATE_DRAWERS.VerifyUrgency),
    visible: permissions.canVerifyUrgency
  },
  {
    title: canManage ? COPIES.menu.manageRecruiters : COPIES.menu.viewRecruiters,
    action: openDrawer(CANDIDATE_DRAWERS.Recruiters),
    visible: canManage || canView
  },
  {
    title: hasAssistant ? COPIES.menu.manageCollaborator : COPIES.menu.addCollaborator,
    action: openDrawer(CANDIDATE_DRAWERS.Recruiters),
    visible: canCollaborate
  },
  ...searchProjectMenuItems,
  {
    title: COPIES.common.referenceRelease,
    action: onOpenReferenceRelease,
    visible: permissions.canOpenReferenceRelease
  },
  {
    title: COPIES.common.haAddition,
    action: openDrawer(CANDIDATE_DRAWERS.AddHiringAuthority),
    visible: !permissions.hasHiringAuthority
  },
  {
    title: COPIES.menu.linkHiringAuthority,
    action: openDrawer(CANDIDATE_DRAWERS.LinkHiringAuthority),
    visible: !permissions.hasHiringAuthority
  },
  {
    title: COPIES.menu.unlinkHiringAuthority,
    action: onUnlinkHiringAuthority,
    visible: permissions.hasHiringAuthority
  },
  {
    title: COPIES.menu.addAction,
    action: openDrawer(CANDIDATE_DRAWERS.Task),
    visible: permissions.canUseDailyPlan
  },
  {
    title: strings.ai.generators.roleplay.actionButton,
    action: openDrawer(CANDIDATE_DRAWERS.Roleplay),
    visible: permissions.canUseRoleplay
  },
  {
    title: COPIES.accountability.action,
    action: openDrawer(CANDIDATE_DRAWERS.Claim),
    visible: permissions.canClaimAccountability
  },
  ...restrictionMenuItems
];
